import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Send, X, Bot, User, Sparkles, Loader2, ExternalLink, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const renderTextWithLinks = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline hover:text-blue-800 break-all"
      >
        {part}
      </a>
    ) : (
      part
    )
  );
};

const SUGGESTION_POOL = [
  {
    id: 'lead-status',
    label: 'Come seguire una lead',
    message: 'Mi spieghi come apro una lead, controllo lo stato e aggiungo note in Accelera?'
  },
  {
    id: 'doc-request',
    label: 'Chiedere documenti',
    message: 'Quali passi devo fare per chiedere documenti a un cliente e vedere se li ha caricati?'
  },
  {
    id: 'my-leads',
    label: 'Dove trovo i miei lead',
    message: 'Come arrivo rapidamente alla pagina "My Leads" per vedere le pratiche assegnate a me?'
  },
  {
    id: 'contact-client',
    label: 'Contattare il cliente',
    message: 'C’è un modo semplice per contattare il cliente dalla scheda lead?'
  },
  {
    id: 'upload-help',
    label: 'Cliente non carica i file',
    message: 'Cosa posso dire a un cliente che non riesce a caricare i documenti dal link Accelera?'
  },
  {
    id: 'notifications',
    label: 'Notifiche e email',
    message: 'Come faccio a sapere quando un cliente aggiorna la sua pratica o carica un file?'
  }
];

const SESSION_STORAGE_KEY = 'eugenioChatSession';
const SESSION_TTL_MS = 15 * 60 * 1000;

const pickRandomSuggestions = (count = 4) => {
  const pool = [...SUGGESTION_POOL];
  const suggestions = [];
  while (pool.length > 0 && suggestions.length < count) {
    const index = Math.floor(Math.random() * pool.length);
    suggestions.push(pool.splice(index, 1)[0]);
  }
  return suggestions;
};

const createWelcomeMessage = () => ({
  id: Date.now(),
  text: "Ciao! Sono Eugenio, il tuo assistente AI per Creditplan.\n\nQuesta è la mia prima versione, quindi le risposte potrebbero non essere sempre 100% accurate.\n\nHo nel mio database tutto il Kit Commerciale 2025, quindi puoi farmi qualsiasi domanda su mutui, prestiti personali, assicurazioni e condizioni delle banche convenzionate.\n\nCome posso aiutarti?",
  sender: 'bot',
  timestamp: new Date(),
  suggestions: []
});

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => [createWelcomeMessage()]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollAreaRef = useRef(null);
  const inputRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        return;
      }
      if (Array.isArray(parsed.messages) && parsed.messages.length > 0) {
        const restoredMessages = parsed.messages.map(msg => ({
          ...msg,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
        }));
        setMessages(restoredMessages);
      }
      if (parsed.isOpen) {
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Failed to restore chatbot session:', error);
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      if (!isFirstRenderRef.current) {
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }
      return;
    }

    const payload = {
      messages: messages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp
      })),
      isOpen: true,
      expiresAt: Date.now() + SESSION_TTL_MS
    };
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error('Failed to persist chatbot session:', error);
    }
    isFirstRenderRef.current = false;
  }, [messages, isOpen]);

  // Auto-scroll to bottom when new messages arrive or typing indicator appears
  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollAreaRef.current) {
        const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          requestAnimationFrame(() => {
            viewport.scrollTop = viewport.scrollHeight;
          });
        }
      }
    };
    
    scrollToBottom();
  }, [messages, isTyping]);

  // Focus input when sheet opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  const handleSendMessage = async (overrideText = null) => {
    const rawText = overrideText ?? inputValue;
    if (!rawText?.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      text: rawText.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    if (!overrideText) {
      setInputValue('');
    }
    setIsTyping(true);

    try {
      // Get auth token
      const { auth } = await import('@/auth/firebase');
      if (!auth.currentUser) {
        throw new Error('User session expired');
      }
      
      const token = await auth.currentUser.getIdToken(true);
      const { getApiUrl } = await import('@/config');

      // Build conversation history (last 10 messages for context, including the current message)
      const conversationHistory = [
        ...messages.slice(-9), // Last 9 messages from state
        userMessage // Include the current user message
      ].map(msg => ({
        sender: msg.sender,
        text: msg.text
      }));

      // Call OpenAI chatbot API
      const response = await fetch(getApiUrl('api/chatbot/message'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage.text,
          conversationHistory: conversationHistory
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Errore durante la richiesta' }));
        throw new Error(errorData.error || errorData.message || 'Errore durante la richiesta');
      }

      const result = await response.json();

      const botResponse = {
        id: Date.now() + 1,
        text: result.response || 'Mi dispiace, non ho ricevuto una risposta valida.',
        sender: 'bot',
        timestamp: new Date(),
        documents: result.documents || [], // Include documents if any
        actions: result.actions || []
      };

      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Chatbot error:', error);
      
      // Show error message to user
      const errorResponse = {
        id: Date.now() + 1,
        text: error.message.includes('session expired') 
          ? 'La tua sessione è scaduta. Per favore, ricarica la pagina e riprova.'
          : 'Mi dispiace, si è verificato un errore. Riprova più tardi.',
        sender: 'bot',
        timestamp: new Date(),
        actions: []
      };
      
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (message) => {
    handleSendMessage(message);
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      setMessages([createWelcomeMessage()]);
      setInputValue('');
      setTimeout(() => {
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }, 0);
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              onClick={() => setIsOpen(true)}
              className="fixed bottom-6 right-6 h-14 px-6 rounded-full bg-white text-blue-900 z-50 flex items-center justify-center shadow-lg hover:bg-white hover:text-blue-900 active:bg-white"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <img
                  src="https://creditplan.it/wp-content/uploads/2025/10/Eugenio.svg"
                  alt="Eugenio"
                  className="h-10 w-20 object-contain"
                />
              )}
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-rose-200 text-rose-900 rounded-full flex items-center justify-center text-xs font-semibold">
                1
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Apri chat assistente</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Chat Sheet */}
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col bg-gradient-to-b from-blue-50 via-slate-50 to-pink-50">
          <SheetHeader className="px-6 pt-6 pb-4 bg-white/60">
              <div className="flex items-center gap-3">
                <div className="relative">
                 <Avatar className="h-10 w-10 bg-blue-100 text-blue-900">
                   <AvatarFallback className="bg-blue-100 text-blue-900">
                     <Bot className="h-5 w-5" />
                   </AvatarFallback>
                 </Avatar>
                 <Badge 
                  className="absolute -bottom-1 -right-1 h-4 w-4 p-0 bg-green-200 text-green-900"
                  variant="default"
                 />
                </div>
                <div className="flex-1">
               <SheetTitle className="text-left">
                   <img
                     src="https://creditplan.it/wp-content/uploads/2025/10/Eugenio.svg"
                     alt="Eugenio"
                     className="h-10 object-contain"
                   />
                 </SheetTitle>
                </div>
              </div>
          </SheetHeader>

          {/* Messages Area */}
          <ScrollArea className="flex-1 px-4 py-4 bg-white/30" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.sender === 'bot' && (
                    <Avatar className="h-8 w-8 shrink-0 bg-blue-100 text-blue-900">
                      <AvatarFallback className="bg-blue-100 text-blue-900">
                        <Bot className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  
                  <Card
                    className={`max-w-[80%] rounded-2xl border-none shadow-none ${
                      message.sender === 'user'
                        ? 'bg-blue-100 text-blue-900'
                        : 'bg-rose-50 text-slate-900'
                    }`}
                  >
                    <CardContent className="p-4">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {renderTextWithLinks(message.text)}
                      </p>
                      {message.suggestions && message.suggestions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {message.suggestions.map((suggestion) => (
                            <button
                              key={suggestion.id}
                              onClick={() => handleSuggestionClick(suggestion.message)}
                              className="text-xs font-medium px-4 py-2 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              {suggestion.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {message.actions && message.actions.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-semibold text-slate-600">
                            Apri subito:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {message.actions.map((action) => (
                              <Link
                                key={action.id}
                                to={action.url}
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                              >
                                <span>{action.label}</span>
                                <ExternalLink className="h-3 w-3 text-blue-400" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Document Links */}
                      {message.documents && message.documents.length > 0 && (
                        <div className="mt-3 space-y-2 pt-3">
                          <p className="text-xs font-semibold text-slate-700 mb-2">
                            Documenti disponibili:
                          </p>
                          {message.documents.map((doc, idx) => (
                              <a
                                key={idx}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 text-blue-900 hover:bg-blue-100 transition-colors group"
                              >
                              <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-900 truncate">
                                  {doc.documentName}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  {doc.personName}
                                </p>
                              </div>
                              <ExternalLink className="h-3 w-3 text-blue-400 group-hover:text-blue-600 shrink-0" />
                            </a>
                          ))}
                        </div>
                      )}
                      
                      <p
                        className={`text-xs mt-2 ${
                          message.sender === 'user'
                            ? 'text-blue-500'
                            : 'text-slate-500'
                        }`}
                      >
                        {formatTime(message.timestamp)}
                      </p>
                    </CardContent>
                  </Card>

                  {message.sender === 'user' && (
                    <Avatar className="h-8 w-8 shrink-0 bg-blue-200 text-blue-900">
                      <AvatarFallback className="bg-blue-200 text-blue-900">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex gap-3 justify-start">
                  <Avatar className="h-8 w-8 shrink-0 bg-blue-100 text-blue-900">
                    <AvatarFallback className="bg-blue-100 text-blue-900">
                      <Bot className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                  <Card className="bg-rose-50 border-none shadow-none max-w-[80%] rounded-2xl">
                    <CardContent className="p-4">
                      <div className="flex gap-1">
                        <Loader2 className="h-4 w-4 animate-spin text-rose-400" />
                        <span className="text-sm text-slate-600">Sto scrivendo...</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="p-4">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1 bg-transparent border-none shadow-none focus-visible:ring-0 focus-visible:outline-none placeholder:text-slate-500 text-slate-900"
                disabled={isTyping}
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isTyping}
                      className="bg-transparent text-blue-700 hover:bg-blue-50 shrink-0"
                      size="icon"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Invia messaggio (Invio)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Premi Invio per inviare • Shift+Invio per andare a capo
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ChatbotWidget;

